import { formatDate, formatTime } from '@seen/core/dates'
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  cn,
  KBD,
  TypographyDetail,
} from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import { isToday } from 'date-fns'
import { type JSX, type ReactNode, useCallback, useMemo, useRef } from 'react'
import { ContextMenuOpenInNewTab } from '#src/components/ContextMenuOpenInNewTab.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconInboxRead } from '#src/components/icons/IconInboxRead.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { archiveInboxItemsVerified } from '#src/hooks/inbox/inboxLiveRemoval.ts'
import { useInboxArchiveAdvance } from '#src/hooks/inbox/useInboxArchiveAdvance.ts'
import { useInboxSiblings } from '#src/hooks/inbox/useInboxSiblings.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useKnockFeedClient } from '#src/hooks/useKnockFeedClient.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { mergeRefs } from '#src/utils/mergeRefs.ts'

export interface InboxRowProps {
  to: string
  notificationItem: NotificationItem
  children: ReactNode
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
  ghost?: boolean
}

export function InboxRow({
  to,
  notificationItem,
  children,
  onSelect,
  isSelected,
  sidebarOpen,
  ghost,
}: InboxRowProps): JSX.Element {
  const feedClient = useKnockFeedClient()
  const getSiblings = useInboxSiblings()
  const advanceSelectionAwayFrom = useInboxArchiveAdvance()

  /* Resolve siblings once (stable across callbacks). Includes the item itself
     when siblings exist; falls back to just the item when there are none. */
  const allItems = useMemo(() => {
    const siblings = getSiblings(notificationItem)
    return siblings.length > 0 ? siblings : [notificationItem]
  }, [getSiblings, notificationItem])

  const markRead = useCallback(() => {
    const unread = allItems.filter((s) => !s.read_at)
    if (unread.length > 0) {
      void feedClient.markAsRead(unread)
    }
  }, [feedClient, allItems])

  const markUnread = useCallback(() => {
    const read = allItems.filter((s) => !!s.read_at)
    if (read.length > 0) {
      void feedClient.markAsUnread(read)
    }
  }, [feedClient, allItems])

  const markArchived = useCallback(() => {
    const unarchived = allItems.filter((s) => !s.archived_at)
    if (unarchived.length > 0) {
      advanceSelectionAwayFrom(notificationItem.id)
      void archiveInboxItemsVerified(feedClient, unarchived)
    }
  }, [feedClient, allItems, advanceSelectionAwayFrom, notificationItem.id])

  const markUnarchived = useCallback(() => {
    const archived = allItems.filter((s) => !!s.archived_at)
    if (archived.length > 0) {
      advanceSelectionAwayFrom(notificationItem.id)
      void feedClient.markAsUnarchived(archived)
    }
  }, [feedClient, allItems, advanceSelectionAwayFrom, notificationItem.id])

  const toggleDoneHotkeyRef = useHotkeys(
    Hotkeys.ToggleInboxDone,
    () => {
      if (!notificationItem.archived_at) {
        markArchived()
      } else {
        markUnarchived()
      }
    },
    { enabled: !sidebarOpen }
  )
  const toggleReadHotkeyRef = useHotkeys(
    Hotkeys.ToggleInboxRead,
    () => {
      if (!notificationItem.read_at) {
        markRead()
      } else {
        markUnread()
      }
    },
    { enabled: !sidebarOpen }
  )

  const ref = useRef<HTMLAnchorElement>(null)
  /* A selectable row is a focusable div, not a <button>: it contains the ✓ /
     read action <Button>s, and a <button> may not be a descendant of a
     <button> (React hydration error + invalid HTML). */
  const rowRef = useRef<HTMLDivElement>(null)

  const interior = (
    <InboxRowInterior
      notificationItem={notificationItem}
      markArchived={markArchived}
      markUnarchived={markUnarchived}
      markRead={markRead}
      markUnread={markUnread}
    >
      {children}
    </InboxRowInterior>
  )

  const contextMenuContent = (
    <ContextMenuContent>
      <MarkDoneItem
        notificationItem={notificationItem}
        markArchived={markArchived}
        markUnarchived={markUnarchived}
      />
      <MarkReadItem
        notificationItem={notificationItem}
        markRead={markRead}
        markUnread={markUnread}
      />
      <ContextMenuSeparator />
      <ContextMenuOpenInNewTab href={to} />
    </ContextMenuContent>
  )

  if (onSelect) {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {/* a <button> may not contain the ✓/read action <button>s, so the selectable row is a focusable div with role="button" */}
          <div
            role="button"
            tabIndex={0}
            ref={mergeRefs([rowRef, toggleDoneHotkeyRef, toggleReadHotkeyRef])}
            onMouseEnter={() => rowRef.current?.focus()}
            onMouseLeave={() => {
              if (document.activeElement === rowRef.current) {
                rowRef.current?.blur()
              }
            }}
            onClick={() => {
              markRead()
              onSelect()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                markRead()
                onSelect()
              }
            }}
            className={cn(
              'group flex w-full items-center gap-3 px-pagex-default text-left hover:bg-accent focus:bg-accent focus:outline-hidden data-[state=open]:bg-accent lg:px-pagex-lg',
              isSelected && 'bg-accent',
              ghost && 'opacity-60'
            )}
          >
            {interior}
          </div>
        </ContextMenuTrigger>
        {contextMenuContent}
      </ContextMenu>
    )
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Link
          ref={mergeRefs([ref, toggleDoneHotkeyRef, toggleReadHotkeyRef])}
          onMouseEnter={() => ref.current?.focus()}
          onMouseLeave={() => {
            if (document.activeElement === ref.current) {
              ref.current?.blur()
            }
          }}
          to={to}
          onClick={() => markRead()}
          className={cn(
            'group flex items-center gap-3 px-pagex-default hover:bg-accent focus:bg-accent focus:outline-hidden data-[state=open]:bg-accent lg:px-pagex-lg',
            ghost && 'opacity-60'
          )}
        >
          {interior}
        </Link>
      </ContextMenuTrigger>
      {contextMenuContent}
    </ContextMenu>
  )
}

interface InboxRowInteriorProps {
  notificationItem: NotificationItem
  children: ReactNode
  markArchived: () => void
  markUnarchived: () => void
  markRead: () => void
  markUnread: () => void
}

function InboxRowInterior({
  notificationItem,
  children,
  markArchived,
  markUnarchived,
  markRead,
  markUnread,
}: InboxRowInteriorProps): JSX.Element {
  return (
    <>
      {children}
      <div>
        <TypographyDetail className="leading-4 text-muted-foreground group-hover:hidden">
          {isToday(notificationItem.inserted_at)
            ? formatTime(notificationItem.inserted_at)
            : formatDate(notificationItem.inserted_at)}
        </TypographyDetail>
        <div className="-mr-3 hidden group-hover:flex">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full hover:bg-background hover:shadow-sm"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  if (!notificationItem.archived_at) {
                    markArchived()
                  } else {
                    markUnarchived()
                  }
                }}
                aria-label={
                  notificationItem.archived_at
                    ? 'Mark as not done'
                    : 'Mark as done'
                }
                data-dd-action-name="inbox.row.toggle_done"
              >
                <IconCheck size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              className="flex items-center gap-2 px-2"
              side="bottom"
            >
              <TypographyDetail className="text-muted-foreground">
                {!notificationItem.archived_at
                  ? 'Mark as done'
                  : 'Mark as not done'}
              </TypographyDetail>
              <KBD>E</KBD>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full hover:bg-background hover:shadow-sm"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  if (!notificationItem.read_at) {
                    markRead()
                  } else {
                    markUnread()
                  }
                }}
                aria-label={
                  notificationItem.read_at ? 'Mark as unread' : 'Mark as read'
                }
                data-dd-action-name="inbox.row.toggle_read"
              >
                <IconInboxRead />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              className="flex items-center gap-2 px-2"
              side="bottom"
            >
              <TypographyDetail className="text-muted-foreground">
                {!notificationItem.read_at ? 'Mark as read' : 'Mark as unread'}
              </TypographyDetail>
              <KBD>U</KBD>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </>
  )
}

function MarkReadItem({
  notificationItem,
  markRead,
  markUnread,
}: {
  notificationItem: NotificationItem
  markRead: () => void
  markUnread: () => void
}): JSX.Element {
  return (
    <ContextMenuItem
      onSelect={() => {
        if (!notificationItem.read_at) {
          markRead()
        } else {
          markUnread()
        }
      }}
      className="gap-2"
    >
      <IconInboxRead />
      <div>{!notificationItem.read_at ? 'Mark as read' : 'Mark as unread'}</div>
    </ContextMenuItem>
  )
}

function MarkDoneItem({
  notificationItem,
  markArchived,
  markUnarchived,
}: {
  notificationItem: NotificationItem
  markArchived: () => void
  markUnarchived: () => void
}): JSX.Element {
  return (
    <ContextMenuItem
      onSelect={() => {
        if (!notificationItem.archived_at) {
          markArchived()
        } else {
          markUnarchived()
        }
      }}
      className="gap-2"
    >
      <IconCheck size={16} />
      <div>{!notificationItem.archived_at ? 'Mark done' : 'Mark not done'}</div>
    </ContextMenuItem>
  )
}
